import React, { useCallback, useEffect, useRef, useState } from 'react';
import { colors, fonts, playful } from '../styles/theme';
import { useIsMobile } from '../hooks';

const features = [
  {
    label: 'Build sentences',
    image: 'sentences',
    imageHeight: 1956,
    title: 'Build your first sentences.',
    description:
      'Start with English meaning and readable transliteration. Build sentences before you learn a new script.',
    color: '#FFF0DF',
    accent: '#A54B00',
  },
  {
    label: 'Practice conversations',
    image: 'conversations',
    imageHeight: 2221,
    title: 'Practise a conversation.',
    description:
      'Practise conversations with hints and translations when you need them.',
    color: '#EAF6FF',
    accent: '#176194',
  },
  {
    label: 'Explore lessons',
    image: 'lessons',
    imageHeight: 2036,
    title: 'Choose a lesson.',
    description:
      'Practice for family calls, meeting new people, ordering food, and your next trip.',
    color: '#F0EDFF',
    accent: '#6543A0',
  },
  {
    label: 'Build a habit',
    image: 'habit',
    imageHeight: 2036,
    title: 'Keep track of your practice.',
    description:
      'Keep track of your streak, goals, and practice time. Come back for another little win.',
    color: '#EAF5E5',
    accent: '#41692B',
  },
  {
    label: 'See your progress',
    image: 'progress',
    imageHeight: 2036,
    title: 'See your recent lessons.',
    description:
      'See your course, completed lessons, and recent activity in one place.',
    color: '#FFEDEF',
    accent: '#A53F53',
  },
  {
    label: 'Find your next words',
    image: 'home',
    imageHeight: 2388,
    title: 'What should we talk about?',
    description: 'Start your next lesson, explore everyday phrases, or practise a conversation with Hathi.',
    color: '#FFF4E4',
    accent: '#A54B00',
  },
];

function FeatureScreenshot({ feature }) {
  const image = `/app-screens/${feature.image}`;
  return <div style={{ background: feature.color, borderRadius: 24, padding: '24px 16px', textAlign: 'center' }}>
    <img src={`${image}-430.webp`} srcSet={`${image}-430.webp 430w, ${image}-860.webp 860w`}
      sizes="(max-width: 480px) 240px, 280px" width={1206} height={feature.imageHeight}
      loading="lazy" decoding="async" alt={`Bhol app: ${feature.label.toLowerCase()}`}
      style={{ display: 'block', width: 'min(100%, 280px)', height: 'auto', margin: '0 auto', borderRadius: 24 }} />
  </div>;
}

export function FeatureCarousel() {
  const isMobile = useIsMobile();
  const track = useRef(null);
  const section = useRef(null);
  const [selected, setSelected] = useState(0);
  const [paused, setPaused] = useState(false);
  const [focused, setFocused] = useState(false);
  const [interaction, setInteraction] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [visible, setVisible] = useState(false);
  const [pageVisible, setPageVisible] = useState(() => !document.hidden);
  const [reducedMotion, setReducedMotion] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const preference = () => setReducedMotion(query.matches);
    const visibility = () => setPageVisible(!document.hidden);
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting && entry.intersectionRatio >= 0.35),
      { threshold: 0.35 }
    );
    observer.observe(section.current);
    query.addEventListener('change', preference);
    document.addEventListener('visibilitychange', visibility);
    return () => {
      observer.disconnect();
      query.removeEventListener('change', preference);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, []);

  useEffect(() => {
    if (!paused) return undefined;
    const timer = window.setTimeout(() => setPaused(false), 10000);
    return () => window.clearTimeout(timer);
  }, [paused, interaction]);

  const pauseTemporarily = () => { setPaused(true); setInteraction(value => value + 1); };
  const playing =
    !paused && !focused && !hovered && visible && pageVisible && !reducedMotion;
  const goTo = useCallback(
    (index, automatic = false) => {
      const next = (index + features.length) % features.length;
      const container = track.current;
      const card = container?.children[next];
      if (!card) return;
      if (!automatic) { setPaused(true); setInteraction(value => value + 1); }
      container.scrollTo({
        left:
          card.getBoundingClientRect().left -
          container.getBoundingClientRect().left +
          container.scrollLeft,
        behavior:
          reducedMotion || (automatic && next === 0) ? 'instant' : 'smooth',
      });
      setSelected(next);
    },
    [reducedMotion]
  );

  useEffect(() => {
    if (!playing) return undefined;
    const timer = window.setTimeout(() => goTo(selected + 1, true), 6500);
    return () => window.clearTimeout(timer);
  }, [playing, selected, goTo]);

  const handleScroll = () => {
    const container = track.current;
    const width = container.children[0]?.getBoundingClientRect().width + 20;
    if (width)
      setSelected(
        Math.min(
          features.length - 1,
          Math.max(0, Math.round(container.scrollLeft / width))
        )
      );
  };

  return (
    <section
      ref={section}
      aria-roledescription="carousel"
      aria-labelledby="features-heading"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}
      style={{ padding: '56px 0' }}
    >
      <h2
        id="features-heading"
        style={{
          ...playful.heading,
          fontSize: 'clamp(1.9rem, 4vw, 3rem)',
          textAlign: 'center',
        }}
      >
        Take a look inside Bhol.
      </h2>
      <p
        style={{
          color: colors.textLight,
          textAlign: 'center',
          margin: '16px auto 28px',
          maxWidth: 530,
        }}
      >
        See how lessons, conversations, and progress tracking work in Bhol.
      </p>
      <div
        ref={track}
        tabIndex={0}
        aria-label="App features. Swipe or use the previous and next buttons."
        onScroll={handleScroll}
        onPointerDown={pauseTemporarily}
        onWheel={pauseTemporarily}
        onKeyDown={(event) => {
          if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            event.preventDefault();
            goTo(selected + (event.key === 'ArrowRight' ? 1 : -1));
          }
        }}
        style={{
          position: 'relative',
          display: 'flex',
          overflowX: 'auto',
          gap: 20,
          scrollSnapType: 'x mandatory',
          overscrollBehaviorX: 'contain',
          padding: '0 0 14px',
        }}
      >
        {features.map((feature, index) => (
          <article
            key={feature.label}
            role="group"
            aria-roledescription="slide"
            aria-label={`${index + 1} of ${features.length}: ${feature.label}`}
            style={{
              flex: '0 0 100%',
              minWidth: 0,
              scrollSnapAlign: 'start',
              ...playful.card,
              padding: 'clamp(20px, 4vw, 36px)',
            }}
          >
            <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', alignItems: 'center', gap: 28 }}>
              <FeatureScreenshot feature={feature} />
              <div>
              <p
                style={{
                  color: feature.accent,
                  fontSize: 13,
                  fontWeight: 800,
                  margin: '24px 0 8px',
                }}
              >
                {feature.label}
              </p>
              <h3
                style={{
                  ...playful.heading,
                  fontSize: 'clamp(1.45rem, 3vw, 2rem)',
                }}
              >
                {feature.title}
              </h3>
              <p
                style={{
                  color: colors.textLight,
                  marginBottom: 0,
                  lineHeight: 1.65,
                }}
              >
                {feature.description}
              </p>
              </div>
            </div>
          </article>
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          gap: 10,
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: 20,
        }}
      >
        <button
          type="button"
          aria-label="Previous feature"
          onClick={() => goTo(selected - 1)}
          style={playful.smallButton}
        >
          ←
        </button>
        <span style={{ fontWeight: 750, minWidth: 54, textAlign: 'center' }}>
          {selected + 1} / {features.length}
        </span>
        <button
          type="button"
          aria-label="Next feature"
          onClick={() => goTo(selected + 1)}
          style={playful.smallButton}
        >
          →
        </button>
      </div>
      <p
        aria-live={playing ? 'off' : 'polite'}
        aria-atomic="true"
        style={{
          textAlign: 'center',
          color: colors.textLight,
          fontFamily: fonts.body,
          fontSize: 13,
        }}
      >
        {features[selected].label} · Swipe to explore
      </p>
    </section>
  );
}
